import { ArrowDownThin } from '@ori-ui/icons';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback } from 'react';

import { useIsMobile } from '../../../hooks';
import { logUserClick } from '../../../utils';
import {
  DESKTOP_SUBCATEGORY_ROOT,
  MOBILE_SUBCATEGORY_ROOT,
  SUBCATEGORY_LIST_ELEMENT,
  SUBCATEGORY_LIST_ELEMENT_TITLE,
  SUBCATEGORY_SEGMENT,
} from './constants';
import {
  SubCategoryIconWrapper,
  SubCategoryListElement,
  SubCategoryRoot,
  SubCategorySegment,
  SubCategoryTitle,
} from './styles';
import type { SubCategoryProps } from './types';

export const SubCategory: FC<SubCategoryProps> = ({
  subCategory,
  activeSubCategory,
  handleSubCategoryClick,
  displayArrowIcon = true,
  boldText = true,
  isBestOffers,
}) => {
  const isMobile = useIsMobile();

  const { getTestId } = useTestId();
  const isSubCategoryActive = activeSubCategory === subCategory.url;
  const ownerState = { isSubCategoryActive };

  const analyticsData = useCallback(
    (url: string) => () => {
      logUserClick(url);
    },
    [],
  );

  const subCategoryClickHandler = useCallback(() => {
    handleSubCategoryClick(subCategory.url);
  }, [handleSubCategoryClick, subCategory.url]);

  if (!subCategory.url) {
    return null;
  }

  if (isMobile) {
    return (
      <SubCategoryRoot data-testid={getTestId(MOBILE_SUBCATEGORY_ROOT)}>
        <SubCategoryListElement
          data-testid={getTestId(SUBCATEGORY_LIST_ELEMENT)}
          onClick={subCategoryClickHandler}
        >
          <SubCategoryTitle
            boldText={boldText}
            href={subCategory.url}
            data-testid={getTestId(SUBCATEGORY_LIST_ELEMENT_TITLE)}
            onClick={analyticsData(subCategory.url)}
          >
            {subCategory.title}
          </SubCategoryTitle>
          {displayArrowIcon ? (
            <SubCategoryIconWrapper ownerState={ownerState}>
              <ArrowDownThin fontSize="inherit" />
            </SubCategoryIconWrapper>
          ) : null}
        </SubCategoryListElement>
        {isSubCategoryActive ? (
          <>
            {subCategory.children.map(
              (segment) =>
                segment.url && (
                  <SubCategorySegment
                    key={segment.id}
                    isBestOffers={isBestOffers}
                    href={segment.url}
                    data-testid={getTestId(SUBCATEGORY_SEGMENT)}
                    onClick={analyticsData(segment.url)}
                  >
                    {segment.title}
                  </SubCategorySegment>
                ),
            )}
          </>
        ) : null}
      </SubCategoryRoot>
    );
  }

  return (
    <SubCategoryRoot data-testid={getTestId(DESKTOP_SUBCATEGORY_ROOT)}>
      <SubCategoryListElement>
        <SubCategoryTitle
          boldText={boldText}
          href={subCategory.url}
          data-testid={getTestId(SUBCATEGORY_LIST_ELEMENT_TITLE)}
          onClick={analyticsData(subCategory.url)}
        >
          {subCategory.title}
        </SubCategoryTitle>
      </SubCategoryListElement>
      {subCategory.children.map(
        (segment) =>
          segment.url && (
            <SubCategoryListElement
              key={segment.id}
              data-testid={getTestId(SUBCATEGORY_LIST_ELEMENT)}
              onClick={analyticsData(segment.url)}
            >
              <SubCategorySegment href={segment.url}>{segment.title}</SubCategorySegment>
            </SubCategoryListElement>
          ),
      )}
    </SubCategoryRoot>
  );
};
